import { CustomHeader } from "@/components/CustomHeader";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button } from "@/components/ui/Button";
import CustomModal from "@/components/ui/CustomModal.web";
import type { DefaultHomeTab } from "@/constants/AppNavigation";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import Feather from "@expo/vector-icons/Feather";
import { router } from "expo-router";
import Head from "expo-router/head";
import { getApp } from "firebase/app";
import { doc, getFirestore, setDoc } from "firebase/firestore";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, StyleSheet } from "react-native";

const firestore = getFirestore(getApp());

const SettingsDefaultHomePage = () => {
  const { user, userData } = useAuth();
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [modalInfo, setModalInfo] = useState<{
    title: string;
    message: string;
  } | null>(null);

  const selected: DefaultHomeTab =
    userData?.defaultHomeTab === "groups" ? "groups" : "feed";

  const handleSelect = async (value: DefaultHomeTab) => {
    if (!user || saving || value === selected) return;
    setSaving(true);
    try {
      await setDoc(
        doc(firestore, "users", user.uid),
        { defaultHomeTab: value },
        { merge: true },
      );
      addToast(t("SETTINGS_SAVED"), "success");
    } catch (error) {
      console.error("Error saving default home setting:", error);
      setModalInfo({ title: t("ERROR"), message: t("NETWORK_ERROR") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Head>
        <title>{t("PAGE_TITLE_SETTINGS_DEFAULT_HOME")}</title>
      </Head>
      <CustomHeader
        title={t("DEFAULT_HOME")}
        type="subtitle"
        variant="tab"
        showBackButton
        onBackPress={() => router.replace("/(tabs)/settings")}
      />
      <ThemedView style={styles.container}>
        <ThemedText style={styles.description}>
          {t("DEFAULT_HOME_DESCRIPTION")}
        </ThemedText>

        <OptionRow
          title={t("DEFAULT_HOME_FEED")}
          description={t("DEFAULT_HOME_FEED_DESCRIPTION")}
          selected={selected === "feed"}
          onPress={() => handleSelect("feed")}
          testID="default-home-feed-option"
        />
        <OptionRow
          title={t("DEFAULT_HOME_GROUPS")}
          description={t("DEFAULT_HOME_GROUPS_DESCRIPTION")}
          selected={selected === "groups"}
          onPress={() => handleSelect("groups")}
          testID="default-home-groups-option"
        />
      </ThemedView>
      {modalInfo && (
        <CustomModal
          visible={!!modalInfo}
          onClose={() => setModalInfo(null)}
          title={modalInfo.title}
        >
          <ThemedView>
            <ThemedText>{modalInfo.message}</ThemedText>
            <Button
              text={t("OK")}
              onPress={() => setModalInfo(null)}
              style={{ marginTop: 20 }}
            />
          </ThemedView>
        </CustomModal>
      )}
    </>
  );
};

const OptionRow = ({
  title,
  description,
  selected,
  onPress,
  testID,
}: {
  title: string;
  description: string;
  selected: boolean;
  onPress: () => void;
  testID?: string;
}) => (
  <Pressable
    onPress={onPress}
    accessibilityRole="radio"
    accessibilityState={{ selected }}
    style={styles.optionRow}
    testID={testID}
  >
    <ThemedView style={styles.optionLabel}>
      <ThemedText style={styles.optionTitle}>{title}</ThemedText>
      <ThemedText style={styles.optionDescription}>{description}</ThemedText>
    </ThemedView>
    {selected && (
      <Feather
        name="check"
        size={22}
        color="#0a7ea4"
        testID={testID ? `${testID}-check` : undefined}
      />
    )}
  </Pressable>
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    alignSelf: "center",
    width: "100%",
    maxWidth: 600,
  },
  description: {
    fontSize: 13,
    opacity: 0.75,
    marginBottom: 16,
  },
  optionRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 16,
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
  },
  optionLabel: {
    flex: 1,
  },
  optionTitle: {
    fontSize: 16,
    fontWeight: "600",
  },
  optionDescription: {
    fontSize: 13,
    marginTop: 4,
    opacity: 0.7,
  },
});

export default SettingsDefaultHomePage;
